import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { DatasetAnalyticsResp, DatasetAnalyticsTrendBucket } from "@/api";
import { datasetAnalyticsStore } from "@/components/analytics";
import type { LoadState } from "@/components/dataset/loadState";

export type DatasetAnalyticsController = {
  state: LoadState<DatasetAnalyticsResp>;
  bucket: DatasetAnalyticsTrendBucket;
  setBucket: (bucket: DatasetAnalyticsTrendBucket) => void;
  reload: () => void;
};

/**
 * 대시보드용 조직 전체 데이터셋 통계. 전역 스토어(`datasetAnalyticsStore`)의 버킷 하나를 읽는다.
 *
 * 폴링하지 않는다. 조직 누적 통계는 초 단위로 바뀌지 않는다. 사용자가 갱신을 원하면 헤더의
 * 새로고침을 쓴다. 스토어가 버킷별 응답을 캐시한다. 그래서 `주 ↔ 일` 을 전환할 때 두 번째부터는
 * 요청이 없다. 검색 콤보박스의 추천 라벨도 같은 응답을 읽는다.
 *
 * 버킷을 바꿔 다시 조회하는 동안에도 직전 데이터를 남긴다. 그래서 추이 카드만 잠시 이전 값을
 * 보여 주고, 나머지 카드는 화면에서 사라지지 않는다.
 */
export function useDatasetAnalytics(): DatasetAnalyticsController {
  const [bucket, setBucket] = useState<DatasetAnalyticsTrendBucket>("week");
  const snapshot = useSyncExternalStore(
    datasetAnalyticsStore.subscribe,
    datasetAnalyticsStore.getSnapshot,
    datasetAnalyticsStore.getSnapshot,
  );

  useEffect(() => {
    datasetAnalyticsStore.ensure(bucket);
  }, [bucket]);

  const reload = useCallback(() => {
    void datasetAnalyticsStore.refresh(bucket);
  }, [bucket]);

  const entry = snapshot[bucket];
  let state: LoadState<DatasetAnalyticsResp>;
  if (entry.data !== null) {
    state = { status: "success", data: entry.data };
  } else if (entry.errorMessage !== null) {
    state = { status: "error", message: entry.errorMessage };
  } else {
    // 버킷 전환으로 받는 중이면 직전 버킷의 값을 보여 준다(추이 외 축은 값이 같다).
    const previous = datasetAnalyticsStore.anyData();
    state =
      previous !== null
        ? { status: "success", data: previous }
        : entry.loading
          ? { status: "loading" }
          : { status: "idle" };
  }

  return { state, bucket, setBucket, reload };
}
